import { MutableHighlightHandle } from './highlight-handle.ts';
import {
  type BoxRect,
  type GeometryResolver,
  type HighlightMode,
  type HighlightRange
} from './types.ts';

export interface AddHighlightOptions {
  range: HighlightRange;
  mode: HighlightMode;
  zIndex: number;
  className?: string;
  elementId?: string;
}

/**
 * One current DOM representation of a logical highlight. A range that crosses
 * coordinate spaces has one primary fragment and one or more continuations.
 * Surface names stay private so consumers do not acquire table-layout logic.
 */
export type HighlightElementFragment =
  | {
      readonly role: 'primary';
      readonly element: HTMLElement;
    }
  | {
      readonly role: 'continuation';
      readonly element: HTMLElement;
    };

/**
 * Stable reference returned for one logical highlight ID.
 *
 * Most consumers can ignore this handle. It exists for features such as
 * presence and AI that must attach behavior to the physical DOM elements. The
 * handle survives reprojection when pinning changes and the underlying element
 * set is replaced.
 */
export interface HighlightHandle {
  /**
   * Logical range ID passed to `HighlightsRenderer.add()`.
   */
  readonly id: string;

  /**
   * Binds behavior to every current physical fragment. The callback runs
   * immediately for existing fragments and for replacements created by later
   * reprojection. Returned fragment cleanups run before those fragments leave
   * the DOM; the returned outer cleanup removes the binding itself.
   */
  bindElements(
    binding: (fragment: HighlightElementFragment) => void | (() => void)
  ): () => void;
}

/**
 * Feature-facing API for one logical highlight layer.
 *
 * A renderer owns a group of related highlights with one lifecycle, such as
 * selection backgrounds or presence. Its implementation may project each
 * logical range onto multiple private DOM surfaces without exposing those
 * surfaces to the feature.
 */
export interface HighlightsRenderer {
  /**
   * Adds or replaces a logical highlight and returns its stable handle.
   */
  add(options: AddHighlightOptions): HighlightHandle;
  /**
   * Updates the range and mode for an existing highlight ID.
   */
  update(range: HighlightRange, mode: HighlightMode): void;
  /**
   * Hides the complete logical layer without discarding its highlights.
   */
  hideAll(): void;
  /**
   * Shows the layer and redraws its current highlights.
   */
  showAll(): void;
  /**
   * Remeasures and redraws every active logical highlight.
   */
  redrawAll(): void;
  /**
   * Removes one logical highlight and every physical fragment representing it.
   */
  remove(id: string): void;
  /**
   * Removes every logical highlight owned by this layer.
   */
  clear(): void;
  /**
   * Permanently releases the layer, its fragments, bindings, and registration.
   */
  dispose(): void;
}

/**
 * Physical leaf renderer for exactly one DOM container and geometry resolver.
 *
 * It owns one absolutely-positioned element per range ID and performs direct
 * DOM measurement and writes. It does not know about tables, pinning, or range
 * fragmentation; a rendering strategy composes multiple instances when a
 * logical highlight spans multiple coordinate spaces.
 */
export class DomHighlightsRenderer implements HighlightsRenderer {
  #elements = new Map<string, HTMLElement>();
  #handles = new Map<string, MutableHighlightHandle>();
  #activeRanges = new Map<string, { range: HighlightRange; mode: HighlightMode }>();

  #layerContainer: HTMLElement;
  #resolver: GeometryResolver;
  #onDispose: () => void;

  #rafId: number | null = null;
  // Ranges whose bounds came back null (target cell not in the DOM yet — a
  // paste that creates rows/columns, or a view switch that remounts the
  // table, commits selection state before React mounts the new cells).
  // Re-measured on DOM mutation instead of a guessed frame/time budget, since
  // how long the mount takes depends on load and isn't otherwise bounded.
  #pendingNullBounds = new Set<string>();
  #nullBoundsObserver: MutationObserver | null = null;
  #hidden = false;
  #disposed = false;
  // Callers outside an active rollout experiment can opt out of the retry
  // (see #37398/#39072) to keep its exact pre-fix behavior — a bounds miss
  // is left unresolved rather than retried.
  #nullBoundsRetryEnabled: boolean;

  constructor(
    container: HTMLElement,
    resolver: GeometryResolver,
    onDispose: () => void = () => {},
    enableNullBoundsRetry = true
  ) {
    this.#resolver = resolver;
    this.#onDispose = onDispose;
    this.#nullBoundsRetryEnabled = enableNullBoundsRetry;

    this.#layerContainer = document.createElement('div');
    this.#layerContainer.style.position = 'absolute';
    this.#layerContainer.style.top = '0';
    this.#layerContainer.style.left = '0';
    this.#layerContainer.style.pointerEvents = 'none';

    container.appendChild(this.#layerContainer);
  }

  add({
    range,
    mode,
    zIndex,
    className,
    elementId
  }: AddHighlightOptions): HighlightHandle {
    const element = this.addElement({
      range,
      mode,
      zIndex,
      className,
      elementId
    });
    let handle = this.#handles.get(range.id);

    if (!handle) {
      handle = new MutableHighlightHandle(range.id);
      this.#handles.set(range.id, handle);
    }

    handle.setFragments([{ key: range.id, role: 'primary', element }]);

    return handle;
  }

  /**
   * Internal strategy primitive that upserts the physical element without
   * allocating another logical handle.
   */
  addElement({
    range,
    mode,
    zIndex,
    className,
    elementId
  }: AddHighlightOptions): HTMLElement {
    if (this.#disposed) {
      throw new Error('Cannot add a highlight to a disposed renderer.');
    }

    let element = this.#elements.get(range.id);

    if (!element) {
      element = document.createElement('div');
      element.style.position = 'absolute';
      element.style.top = '0';
      element.style.left = '0';
      element.style.pointerEvents = 'none';

      this.#elements.set(range.id, element);
      this.#layerContainer.appendChild(element);
    }

    element.id = elementId ?? '';
    element.className = className ?? '';
    element.style.zIndex = zIndex.toString();

    this.#activeRanges.set(range.id, { range, mode });
    this.update(range, mode);

    return element;
  }

  update(range: HighlightRange, mode: HighlightMode) {
    const element = this.#elements.get(range.id);

    if (!element) {
      return;
    }

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    this.#activeRanges.set(range.id, { range, mode });

    const bounds = this.#measureBounds(range, mode);
    this.#applyBoundsToElement(element, bounds);

    if (this.#nullBoundsRetryEnabled && bounds === null && mode !== 'none') {
      this.#pendingNullBounds.add(range.id);
      this.#ensureNullBoundsObserver();
    } else {
      this.#removePendingNullBounds(range.id);
    }
  }

  hideAll() {
    this.#hidden = true;
    this.#layerContainer.style.display = 'none';
  }

  showAll() {
    this.#hidden = false;
    this.#layerContainer.style.display = '';
    this.redrawAll();
  }

  redrawAll() {
    if (this.#hidden) {
      return;
    }

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
    }

    const pendingWrites: Array<{
      id: string;
      element: HTMLElement;
      bounds: BoxRect | null;
    }> = [];

    this.#activeRanges.forEach(({ range, mode }) => {
      const element = this.#elements.get(range.id);

      if (!element) {
        return;
      }

      const bounds = this.#measureBounds(range, mode);

      pendingWrites.push({ id: range.id, element, bounds });

      if (this.#nullBoundsRetryEnabled && bounds === null && mode !== 'none') {
        this.#pendingNullBounds.add(range.id);
        this.#ensureNullBoundsObserver();
      } else {
        this.#removePendingNullBounds(range.id);
      }
    });

    this.#rafId = requestAnimationFrame(() => {
      pendingWrites.forEach(({ id, element, bounds }) => {
        // Skip a stale null already resolved by the observer in the meantime.
        if (
          bounds === null &&
          this.#nullBoundsRetryEnabled &&
          !this.#pendingNullBounds.has(id)
        ) {
          return;
        }

        this.#applyBoundsToElement(element, bounds);
      });

      this.#rafId = null;
    });
  }

  remove(id: string) {
    this.#handles.get(id)?.dispose();
    this.#elements.get(id)?.remove();
    this.#elements.delete(id);
    this.#handles.delete(id);
    this.#activeRanges.delete(id);
    this.#removePendingNullBounds(id);
  }

  clear() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    this.#pendingNullBounds.clear();
    this.#disconnectNullBoundsObserver();

    this.#handles.forEach((handle) => handle.dispose());
    this.#elements.forEach((element) => element.remove());
    this.#elements.clear();
    this.#handles.clear();
    this.#activeRanges.clear();
  }

  dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;
    this.clear();
    this.#layerContainer.remove();
    this.#onDispose();
  }

  #measureBounds(range: HighlightRange, mode: HighlightMode): BoxRect | null {
    const minRow = Math.min(range.anchor.row, range.lead.row);
    const maxRow = Math.max(range.anchor.row, range.lead.row);
    const minCol = Math.min(range.anchor.col, range.lead.col);
    const maxCol = Math.max(range.anchor.col, range.lead.col);

    if (mode === 'cell') {
      const topLeft = this.#resolver.getCellBounds(minRow, minCol);
      const bottomRight = this.#resolver.getCellBounds(maxRow, maxCol);

      if (topLeft && bottomRight) {
        return {
          x: topLeft.x,
          y: topLeft.y,
          width: bottomRight.x + bottomRight.width - topLeft.x,
          height: bottomRight.y + bottomRight.height - topLeft.y
        };
      }
    } else if (mode === 'row') {
      const top = this.#resolver.getRowBounds(minRow);
      const bottom = this.#resolver.getRowBounds(maxRow);

      if (top && bottom) {
        return {
          x: top.x,
          y: top.y,
          width: top.width,
          height: bottom.y + bottom.height - top.y
        };
      }
    } else if (mode === 'column') {
      const left = this.#resolver.getColumnBounds(minCol);
      const right = this.#resolver.getColumnBounds(maxCol);

      if (left && right) {
        return {
          x: left.x,
          y: left.y,
          width: right.x + right.width - left.x,
          height: left.height
        };
      }
    } else if (mode === 'container') {
      return this.#resolver.getContainerBounds();
    }

    return null;
  }

  /**
   * Watches the scroll container for the DOM churn that resolves a pending
   * null-bounds range (a cell mounting after a paste creates rows/columns, or
   * a view switch remounting the table). Lazily created, and disconnected
   * once nothing is pending — cheaper than polling every frame for an
   * unknown-length remount, and has no arbitrary retry cap to outrun under load.
   */
  #ensureNullBoundsObserver() {
    if (this.#nullBoundsObserver) {
      return;
    }

    const container = this.#resolver.getScrollContainer();

    if (!container) {
      return;
    }

    this.#nullBoundsObserver = new MutationObserver(() => {
      this.#retryPendingNullBounds();
    });
    this.#nullBoundsObserver.observe(container, { childList: true, subtree: true });
  }

  #retryPendingNullBounds() {
    if (this.#hidden) {
      return;
    }

    this.#pendingNullBounds.forEach((id) => {
      const active = this.#activeRanges.get(id);
      const element = this.#elements.get(id);

      if (!active || !element) {
        this.#pendingNullBounds.delete(id);

        return;
      }

      const bounds = this.#measureBounds(active.range, active.mode);

      if (bounds !== null) {
        this.#applyBoundsToElement(element, bounds);
        this.#pendingNullBounds.delete(id);
      }
    });

    if (this.#pendingNullBounds.size === 0) {
      this.#disconnectNullBoundsObserver();
    }
  }

  #disconnectNullBoundsObserver() {
    this.#nullBoundsObserver?.disconnect();
    this.#nullBoundsObserver = null;
  }

  #removePendingNullBounds(id: string) {
    this.#pendingNullBounds.delete(id);

    if (this.#pendingNullBounds.size === 0) {
      this.#disconnectNullBoundsObserver();
    }
  }

  #applyBoundsToElement(element: HTMLElement, bounds: BoxRect | null) {
    if (this.#hidden) {
      return;
    }

    if (bounds) {
      element.style.transform = `translate(${bounds.x}px, ${bounds.y}px)`;
      element.style.width = `${bounds.width}px`;
      element.style.height = `${bounds.height}px`;
      element.style.display = 'block';
    } else {
      element.style.display = 'none';
    }
  }
}
